// NEW_FILE_JUSTIFIED: AI Notes section for ContactPanel — replaces mock Internal Notes with real DB-backed notes
import { BrainCircuit, Pencil, Trash2, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAiNotes } from '@/hooks/useAiNotes';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated types live outside src/
import type { IAiNotesSectionProps, TContactNoteType } from '@/@types/aiReply';

const NOTE_TYPE_LABELS: Record<TContactNoteType, string> = {
  preference: 'Preference',
  context: 'Context',
  relationship: 'Relationship',
  request_history: 'Request History',
};

const NOTE_TYPE_COLORS: Record<TContactNoteType, string> = {
  preference: 'bg-blue-500/10 text-blue-600',
  context: 'bg-purple-500/10 text-purple-600',
  relationship: 'bg-green-500/10 text-green-600',
  request_history: 'bg-orange-500/10 text-orange-600',
};

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${String(minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)}h ago`;
  const days = Math.floor(hours / 24);
  return `${String(days)}d ago`;
}

export function AiNotesSection({ contactIdentifier, platform }: IAiNotesSectionProps) {
  const {
    notes,
    editingNoteId,
    editContent,
    setEditContent,
    startEditing,
    cancelEditing,
    saveEdit,
    deleteNote,
  } = useAiNotes(contactIdentifier, platform);

  const handleDelete = (noteId: Id<'contactNotes'>) => {
    if (window.confirm('Delete this AI note?')) {
      void deleteNote(noteId);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <BrainCircuit size={14} className="text-muted-foreground" aria-hidden="true" />
        <span className="text-foreground text-sm font-semibold">AI Notes</span>
      </div>

      {notes === undefined ? (
        <p className="text-muted-foreground text-[13px]">Loading...</p>
      ) : notes.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">No AI notes yet</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {notes.map(note => (
            <div key={note._id} className="flex flex-col gap-1.5 rounded-xl bg-muted p-3">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className={`border-0 ${NOTE_TYPE_COLORS[note.noteType]}`}>
                  {NOTE_TYPE_LABELS[note.noteType]}
                </Badge>
                <span className="text-foreground-placeholder text-[11px]">
                  Updated {formatTimeAgo(note.updatedAt)}
                </span>
              </div>

              {editingNoteId === note._id ? (
                <div className="flex flex-col gap-2">
                  <Textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="min-h-[60px] text-[13px]"
                    autoFocus
                  />
                  <div className="flex items-center gap-1.5 justify-end">
                    <button
                      aria-label="Save edit"
                      onClick={() => void saveEdit()}
                      className={cn(
                        'flex size-7 items-center justify-center rounded-md',
                        'bg-primary text-primary-foreground hover:bg-primary/90'
                      )}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      aria-label="Cancel edit"
                      onClick={cancelEditing}
                      className={cn(
                        'flex size-7 items-center justify-center rounded-md',
                        'bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30'
                      )}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="group flex items-start justify-between gap-2">
                  <p className="text-muted-foreground text-[13px] flex-1">{note.content}</p>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      aria-label="Edit note"
                      onClick={() => startEditing(note._id, note.content)}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-md',
                        'text-muted-foreground hover:bg-muted-foreground/20'
                      )}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      aria-label="Delete note"
                      onClick={() => handleDelete(note._id)}
                      className={cn(
                        'flex size-6 items-center justify-center rounded-md',
                        'text-muted-foreground hover:bg-destructive/20 hover:text-destructive'
                      )}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
